/**
 * Zamanlama çekirdeği.
 *
 * Motorun doğru olması yetmez; yanlış çalıştırılırsa ölçüm bozulur.
 * Bu dosya motorun README'sinde sayılan dört kuralı uygular:
 *
 *  1. Uyarıcı requestAnimationFrame ile sunulur, damga rAF'ın verdiği t.
 *  2. Tepki zamanı event.timeStamp — performance.now() ile aynı taban.
 *  3. pointerdown/keydown dinlenir, click dinlenmez (click gecikmeli).
 *  4. Kare gözcüsü: kare akışı durursa deneme işaretlenir.
 *
 * REACT KULLANILMIYOR: uyarıcıyı göstermek/gizlemek doğrudan DOM'a
 * yazılır. React'in kendi zamanlaması (batching, concurrent scheduling)
 * ile rAF arasına girmek, gösterim anını birkaç kare kaydırabilir ve
 * ölçtüğümüz şey tepki süresi olmaktan çıkar. Çerçeve React'in,
 * uyarıcının kendisi bizim.
 */

import { STALL_TIMEOUT_MS } from '@nirengi/engine'

export interface Calibration {
  frameMs: number
  refreshHz: number
  /** Kalibrasyon sırasında kaybedilen süre — sistem meşgulse yüksek */
  lostMs: number
  stalled: boolean
}

/** Kalibrasyon en fazla bu kadar sürer */
const CALIBRATION_DEADLINE_MS = 4000

/** Bunun altındaki yenileme hızıyla tepki süresi ölçülmez */
const MIN_USABLE_HZ = 45

/**
 * Oturum başında bir kez: ekranın gerçek yenileme hızını ölç.
 *
 * İki ayrı başarısızlık var ve ikisi de gerçek tarayıcıda yaşandı:
 *
 *  - Kare akışı tamamen durur (pencere arkada): gözcü yakalar.
 *  - Kare akışı sürer ama SANİYEDE BİR kare gelir (arka plan kısıtlaması).
 *    Gözcü bunu yakalamaz, çünkü kare geliyor; ama 90 kare toplamak 90
 *    saniye sürer ve ekran "ölçülüyor" yazısında asılı kalır.
 *
 * Bu yüzden hem boşluk gözcüsü hem TOPLAM SÜRE SINIRI var.
 */
export async function calibrate(frames = 90): Promise<Calibration> {
  const ts: number[] = []
  let stalled = false

  await new Promise<void>((resolve) => {
    let last = performance.now()
    const started = performance.now()
    let done = false
    const finish = () => {
      if (done) return
      done = true
      window.clearInterval(watchdog)
      resolve()
    }

    const watchdog = window.setInterval(() => {
      const now = performance.now()
      if (now - last > STALL_TIMEOUT_MS) {
        stalled = true
        finish()
      } else if (now - started > CALIBRATION_DEADLINE_MS) {
        finish()
      }
    }, 200)

    const tick = (t: number) => {
      if (done) return
      ts.push(t)
      last = performance.now()
      if (ts.length < frames && performance.now() - started < CALIBRATION_DEADLINE_MS) {
        requestAnimationFrame(tick)
      } else {
        finish()
      }
    }
    requestAnimationFrame(tick)
  })

  if (ts.length < 10) {
    return { frameMs: 16.7, refreshHz: 60, lostMs: 0, stalled: true }
  }

  const deltas: number[] = []
  for (let i = 1; i < ts.length; i++) deltas.push(ts[i]! - ts[i - 1]!)
  const sorted = [...deltas].sort((a, b) => a - b)
  const frameMs = sorted[sorted.length >> 1]!
  const lostMs = deltas.filter((d) => d > frameMs * 1.5).reduce((a, d) => a + (d - frameMs), 0)
  const refreshHz = 1000 / frameMs

  // Kare geliyor ama çok seyrek: kısıtlanmış arka plan. Teknik olarak
  // "durmuş" değil, ölçüm için kullanılamaz olduğu kesin.
  if (refreshHz < MIN_USABLE_HZ) stalled = true

  return { frameMs, refreshHz, lostMs, stalled }
}

export interface TrialRun {
  stimulusAtMs: number
  responseAtMs: number | null
  /** Dizi görevlerinde birden çok dokunuş olur */
  response: string | string[] | null
  framesDropped: number
  lagMs: number
  frameMs: number
  hidden: boolean
  stalled: boolean
}

export interface RunOptions {
  /** rAF içinde çağrılır: uyarıcıyı göster (doğrudan DOM yazımı) */
  show: () => void
  /** Uyarıcıyı gizle */
  hide: () => void
  /** Uyarıcının ekranda kalma süresi; 0 ise tepkiye kadar kalır */
  durationMs: number
  /** Tepki penceresi; dolduğunda cevapsız sayılır */
  responseWindowMs: number
  /**
   * Gelen olayın kabul edilip edilmeyeceği ve hangi cevaba karşılık
   * geldiği. null dönerse olay yok sayılır.
   */
  accept?: (ev: PointerEvent | KeyboardEvent) => string | null
  /** Olayların dinleneceği eleman */
  target?: HTMLElement | Document
  /**
   * Tepki geldikten sonra uyarıcı bu kadar süre daha ekranda kalır.
   *
   * Çocuk bastığında ekranın anında boşalması, basışının neye ait
   * olduğunu göremez hale getiriyordu. Uyarıcı kısa süre duruyor ve
   * etrafında "kaydedildi" halkası çıkıyor.
   */
  holdOnResponseMs?: number
  /** Tepki geldiği anda çağrılır (halkayı göstermek için) */
  onResponse?: () => void
}

/**
 * Tek bir denemeyi koşturur.
 *
 * Dinleyiciler uyarıcı GÖSTERİLMEDEN ÖNCE bağlanır. Sebebi: uyarıcıdan
 * önce gelen tuş da kaydedilmeli. O tepki negatif süre üretir ve motor
 * bunu `anticipation` olarak işaretler — teknik arıza değil, dürtüsellik
 * göstergesi.
 */
export function runTrial(cal: Calibration, opts: RunOptions): Promise<TrialRun> {
  const target = opts.target ?? document
  const accept = opts.accept ?? (() => 'response')

  return new Promise<TrialRun>((resolve) => {
    let onset = 0
    let responseAt: number | null = null
    let response: string | null = null
    let framesDropped = 0
    let lagMs = 0
    let hidden = document.visibilityState !== 'visible'
    let stalled = false
    let finished = false
    let lastFrameWall = performance.now()
    let lastFrameTs = 0

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') hidden = true
    }

    const onInput = (ev: Event) => {
      if (responseAt !== null) return
      const r = accept(ev as PointerEvent | KeyboardEvent)
      if (r === null) return
      // event.timeStamp performance.now() ile aynı tabanda; Date.now() ASLA
      responseAt = ev.timeStamp
      response = r
      opts.onResponse?.()
      const hold = opts.holdOnResponseMs ?? 0
      if (hold > 0) {
        // Uyarıcıyı erken gizleyecek zamanlayıcıyı iptal et: basışın
        // neye ait olduğu görünsün
        window.clearTimeout(hideTimer)
        window.clearTimeout(windowTimer)
        window.setTimeout(finish, hold)
      } else {
        finish()
      }
    }

    const cleanup = () => {
      document.removeEventListener('visibilitychange', onVisibility)
      target.removeEventListener('pointerdown', onInput as EventListener)
      document.removeEventListener('keydown', onInput as EventListener)
      window.clearInterval(watchdog)
      window.clearTimeout(windowTimer)
      window.clearTimeout(hideTimer)
    }

    const finish = () => {
      if (finished) return
      finished = true
      opts.hide()
      cleanup()
      resolve({
        stimulusAtMs: onset,
        responseAtMs: responseAt,
        response,
        framesDropped,
        lagMs,
        frameMs: cal.frameMs,
        hidden,
        stalled,
      })
    }

    document.addEventListener('visibilitychange', onVisibility)
    target.addEventListener('pointerdown', onInput as EventListener)
    document.addEventListener('keydown', onInput as EventListener)

    /**
     * Kare gözcüsü. Zamanlayıcılar arka plandaki pencerede de (kısılmış
     * olsa bile) çalışır, rAF ise tamamen durabilir — gerçek tarayıcıda
     * ölçtük: visibilityState "visible" iken rAF sekiz saniye hiç
     * tetiklenmedi. Durmayı yakalayan şey bu fark.
     */
    const watchdog = window.setInterval(() => {
      if (performance.now() - lastFrameWall > STALL_TIMEOUT_MS) {
        stalled = true
        finish()
      }
    }, 250)

    let windowTimer = 0
    let hideTimer = 0

    // Uyarıcıyı kareye hizalı göster
    requestAnimationFrame((t) => {
      opts.show()
      onset = t
      lastFrameTs = t
      lastFrameWall = performance.now()

      if (opts.durationMs > 0) {
        hideTimer = window.setTimeout(opts.hide, opts.durationMs)
      }
      windowTimer = window.setTimeout(finish, opts.responseWindowMs)

      const watch = (now: number) => {
        if (finished) return
        const delta = now - lastFrameTs
        if (delta > cal.frameMs * 1.5) {
          framesDropped += Math.round(delta / cal.frameMs) - 1
          lagMs += delta - cal.frameMs
        }
        lastFrameTs = now
        lastFrameWall = performance.now()
        requestAnimationFrame(watch)
      }
      requestAnimationFrame(watch)
    })
  })
}

/** Sunucuya gönderilecek cihaz bilgisi */
export function deviceInfo(cal: Calibration) {
  return {
    ua: navigator.userAgent,
    platform: navigator.platform,
    screen: { w: window.screen.width, h: window.screen.height, dpr: window.devicePixelRatio },
    viewport: { w: window.innerWidth, h: window.innerHeight },
    pointer: window.matchMedia('(pointer: coarse)').matches ? 'touch' : 'fine',
    refreshHz: Number(cal.refreshHz.toFixed(2)),
    frameMs: Number(cal.frameMs.toFixed(3)),
    timeOrigin: Math.round(performance.timeOrigin),
  }
}
